import React, { useState, useEffect } from 'react';
import { 
  FaTrashAlt, FaUsers, FaSearch, FaArrowRight, FaStore, FaChartLine 
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

// --- Types ---
interface Buyer { id: number; username: string; email: string; mobile: string; isVerified: boolean; }
interface Seller { id: number; companyName: string; username: string; email: string; mobile: string; isVerified: boolean; }

const UsersManagement: React.FC = () => {
  const navigate = useNavigate(); // Navigation hook
  const [buyers, setBuyers] = useState<Buyer[]>([]);
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'buyers'|'sellers'>('buyers');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const resBuyers = await fetch('/springboot/api/admin/buyers');
      if(resBuyers.ok) setBuyers(await resBuyers.json());
      const resSellers = await fetch('/springboot/api/admin/sellers-activity');
      if(resSellers.ok) setSellers(await resSellers.json());
    } catch (error) { console.error("Error fetching users:", error); } 
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  // Filter Logic
  const filteredList = (activeTab === 'buyers' ? buyers : sellers).filter((user: any) => {
    const searchLower = searchTerm.toLowerCase();
    const name = user.companyName || user.username || '';
    const email = user.email || '';
    return name.toLowerCase().includes(searchLower) || email.toLowerCase().includes(searchLower);
  });

  return (
    <div className="space-y-6 pb-10 font-sans">
      {/* 1. Compact Header & Tabs */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
        <div>
          <h2 className="text-2xl font-black text-gray-900 tracking-tight">Users Management</h2>
          <p className="text-gray-500 text-xs font-bold mt-1">Total Ecosystem: {buyers.length + sellers.length} Users</p>
        </div>
        
        {/* Modern Pill Tabs */}
        <div className="flex bg-gray-100 p-1 rounded-xl">
           <button 
             onClick={() => setActiveTab('buyers')}
             className={`px-6 py-2 rounded-lg text-xs font-black tracking-widest transition-all ${activeTab === 'buyers' ? 'bg-[#006d5b] text-white shadow-md' : 'text-gray-500 hover:text-gray-800'}`}
           >
             BUYERS
           </button>
           <button 
             onClick={() => setActiveTab('sellers')}
             className={`px-6 py-2 rounded-lg text-xs font-black tracking-widest transition-all ${activeTab === 'sellers' ? 'bg-[#006d5b] text-white shadow-md' : 'text-gray-500 hover:text-gray-800'}`}
           >
             SELLERS
           </button>
        </div>
      </div>

      {/* Smart Summary Bar */}
<div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-6">

  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

    {/* Buyers */}
    <div
      className="flex items-center justify-between p-4 rounded-xl
      bg-gradient-to-r from-emerald-50 to-emerald-100/40
      hover:from-emerald-100 hover:to-emerald-200/50
      transition-all duration-300 cursor-pointer"
    >

      <div className="flex items-center gap-3">

        <div className="p-3 rounded-lg bg-emerald-500 text-white shadow">
          <FaUsers size={18} />
        </div>

        <div>
          <p className="text-xs text-gray-500 font-medium">
            Active Buyers
          </p>

          <h3 className="text-2xl font-bold text-gray-900">
            {buyers.length}
          </h3>
        </div>

      </div>

      <span className="text-xs text-emerald-600 font-semibold">
        +12% ↑
      </span>
    </div>


    {/* Sellers */}
    <div
      className="flex items-center justify-between p-4 rounded-xl
      bg-gradient-to-r from-orange-50 to-orange-100/40
      hover:from-orange-100 hover:to-orange-200/50
      transition-all duration-300 cursor-pointer"
    >

      <div className="flex items-center gap-3">

        <div className="p-3 rounded-lg bg-orange-500 text-white shadow">
          <FaStore size={18} />
        </div>

        <div>
          <p className="text-xs text-gray-500 font-medium">
            Active Sellers
          </p>

          <h3 className="text-2xl font-bold text-gray-900">
            {sellers.length}
          </h3>
        </div>

      </div>

      <span className="text-xs text-orange-600 font-semibold">
        +8% ↑
      </span>
    </div>

  </div>

</div>


      {/* 3. Main List Area (High Contrast) */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex flex-col min-h-[500px]">
        
        {/* Toolbar */}
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/30">
           <h3 className="text-sm font-black text-gray-800 uppercase tracking-wider">
             {activeTab} Directory <span className="text-gray-400 ml-1">({filteredList.length})</span>
           </h3>
           <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={12} />
              <input 
                type="text" 
                placeholder="Search by name..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-white border border-gray-200 rounded-lg py-2 pl-9 pr-4 text-xs font-bold focus:ring-2 focus:ring-[#006d5b]/20 focus:border-[#006d5b] w-64 outline-none transition-all" 
              />
           </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-gray-50 text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-100">
              <tr>
                <th className="px-6 py-4">ID</th>
                <th className="px-6 py-4">Profile Info</th>
                <th className="px-6 py-4">Contacts</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="text-sm divide-y divide-gray-50">
              {filteredList.map((user: any) => (
                <tr key={user.id} className="hover:bg-gray-50/80 transition-colors group">
                  <td className="px-6 py-4 text-xs font-mono text-gray-400">#{user.id}</td>
                  <td className="px-6 py-4">
                     <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center font-black text-gray-600 border border-gray-200 uppercase">
                          {(user.companyName || user.username || "U").charAt(0)}
                        </div>
                        <div>
                           <p className="font-bold text-gray-800 text-sm">{(user.companyName || user.username)}</p>
                           {activeTab === 'sellers' && <p className="text-[10px] text-gray-500 font-bold uppercase">Seller</p>}
                        </div>
                     </div>
                  </td>
                  <td className="px-6 py-4">
                     <p className="text-gray-700 font-semibold text-xs">{user.email}</p>
                     <p className="text-gray-400 text-[10px] font-bold mt-0.5">{user.mobile || "N/A"}</p>
                  </td>
                  <td className="px-6 py-4">
                     {user.isVerified ? (
                       <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded text-[10px] font-black uppercase">Verified</span>
                     ) : (
                       <span className="px-2.5 py-1 bg-amber-50 text-amber-600 border border-amber-100 rounded text-[10px] font-black uppercase">Pending</span>
                     )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {activeTab === 'sellers' ? (
                      // ACTION: Navigate to new page instead of popup
                      <button 
                        onClick={() => navigate('/frog-side/seller-details', { state: { seller: user } })}
                        className="px-4 py-2 bg-[#006d5b] text-white rounded-lg shadow-md hover:bg-[#005a4b] hover:shadow-lg hover:shadow-[#006d5b]/20 transition-all text-[10px] font-black tracking-wide flex items-center gap-2 ml-auto"
                      >
                        VIEW PROFILE <FaArrowRight />
                      </button>
                    ) : (
                      <button className="text-gray-300 hover:text-red-500 transition-colors p-2 hover:bg-red-50 rounded-lg">
                        <FaTrashAlt size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UsersManagement;